import React, { useState, useEffect } from 'react';
import api from '../api/api';
import { Button, Input, Select, Textarea, Label, Icon } from '../components/ui';
import { showConfirm } from '../utils/confirmUtils';
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';

export default function Settings() {
  const [settings, setSettings] = useState({
    companyName: '',
    fiscalId: '',
    address: '',
    phone: '',
    email: '',
    currency: 'TND',
    language: 'fr',
    notifications: true,
    autoBackup: true,
    rib: '',
    bank: '',
    logo: '',
    signature: '',
    footerText: 'Merci de votre confiance',
    paymentTerms: 'Paiement à 30 jours',
    defaultNotes: '',
    eInvoicingConfig: {
      enabled: false,
      ttn: { deliveryMethod: 'SOAP', username: '', password: '', certificatePin: '' },
      digigo: { enabled: false, clientId: '', clientSecret: '', redirectUri: '', defaultSignerEmail: '' }
    }
  });

  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      if (response.data) {
        const merged = {
          ...settings,
          ...response.data,
          eInvoicingConfig: {
            ...settings.eInvoicingConfig,
            ...(response.data.eInvoicingConfig || {}),
            ttn: { ...settings.eInvoicingConfig.ttn, ...(response.data.eInvoicingConfig?.ttn || {}) },
            digigo: { ...settings.eInvoicingConfig.digigo, ...(response.data.eInvoicingConfig?.digigo || {}) }
          }
        };
        setSettings(merged);
        if (response.data.logo) setPreviewUrl(response.data.logo);
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) { setLogoFile(file); setPreviewUrl(URL.createObjectURL(file)); }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleConfigChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      eInvoicingConfig: { ...prev.eInvoicingConfig, [section]: { ...prev.eInvoicingConfig[section], [field]: value } }
    }));
  };

  const handleToggleEInvoice = (e) => {
    setSettings(prev => ({ ...prev, eInvoicingConfig: { ...prev.eInvoicingConfig, enabled: e.target.checked } }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(settings).forEach(key => {
        if (typeof settings[key] === 'object' && settings[key] !== null) {
          formData.append(key, JSON.stringify(settings[key]));
        } else {
          formData.append(key, settings[key]);
        }
      });
      if (logoFile) formData.append('logo', logoFile);
      const response = await api.put('/settings', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert('✅ ' + (response.data.message || 'Paramètres mis à jour'));
      await fetchSettings();
      setLogoFile(null);
    } catch (error) {
      console.error('💥 Erreur COMPLÈTE:', error);
      if (error.response) {
        if (error.response.status === 404) alert('❌ Erreur 404: Route non trouvée. Vérifiez le backend.');
        else if (error.response.status === 401) alert('❌ Erreur 401: Non autorisé. Token invalide.');
        else alert(`❌ Erreur ${error.response.status}: ${error.response.data?.message || 'Erreur serveur'}`);
      } else if (error.request) {
        alert('❌ Serveur inaccessible. Backend démarré ?');
      } else {
        alert('❌ Erreur: ' + error.message);
      }
    } finally { setLoading(false); }
  };

  const handlePasswordUpdate = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) { alert('❌ Les mots de passe ne correspondent pas'); return; }
    if (passwordData.newPassword.length < 6) { alert('❌ Le mot de passe doit faire au moins 6 caractères'); return; }
    setLoading(true);
    try {
      await api.put('/auth/change-password', { currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword });
      alert('✅ Mot de passe changé avec succès !');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Erreur changement mot de passe:', error);
      alert(error.response?.status === 401 ? '❌ Mot de passe actuel incorrect' : '❌ Erreur lors du changement de mot de passe');
    } finally { setLoading(false); }
  };

  const handleGenerateClientAccounts = async () => {
    if (!await showConfirm("Générer les comptes pour tous les clients existants ?")) return;
    try {
      const response = await api.post('/clients/generate-client-accounts');
      alert(`OK: ${response.data.createdCount} comptes créés, ${response.data.skippedExistingUser} déjà existants, ${response.data.skippedNoEmailOrTaxId} sans email/MF`);
    } catch (error) {
      console.error(error);
      alert("Erreur lors de la génération des comptes clients");
    }
  };

  const tabs = [
    { id: 'company', label: 'Informations Cabinet' },
    { id: 'einvoicing', label: 'E-Facturation (TTN/DigiGo)' },
    { id: 'preferences', label: 'Préférences' },
    { id: 'security', label: 'Sécurité' }
  ];

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Paramètres du Cabinet</h1>
        <p className="text-gray-600">Gérez les informations et préférences de votre cabinet</p>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'company' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Informations du Cabinet</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2 flex items-center space-x-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="shrink-0">
                    {previewUrl ? (
                      <img className="h-24 w-24 object-contain border border-gray-200 rounded-lg bg-white" src={previewUrl} alt="Logo cabinet" />
                    ) : (
                      <div className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg bg-white flex items-center justify-center text-gray-400">
                        <span className="text-xs text-center">Aucun<br/>logo</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <Label>Logo du Cabinet</Label>
                    <input type="file" accept="image/*" onChange={handleLogoChange} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer" />
                    <p className="mt-1 text-xs text-gray-500">Ce logo apparaîtra sur vos factures. Formats : PNG, JPG.</p>
                  </div>
                </div>

                <div>
                  <Label required>Nom du Cabinet</Label>
                  <Input type="text" name="companyName" value={settings.companyName} onChange={handleInputChange} placeholder="Nom de votre cabinet" />
                </div>

                <div>
                  <Label required>Matricule Fiscale</Label>
                  <Input type="text" name="fiscalId" value={settings.fiscalId} onChange={handleInputChange} placeholder="Matricule fiscale" />
                </div>

                <div className="md:col-span-2">
                  <Label>Adresse</Label>
                  <Textarea name="address" value={settings.address} onChange={handleInputChange} placeholder="Adresse complète du cabinet" />
                </div>

                <div>
                  <Label>Téléphone</Label>
                  <Input type="tel" name="phone" value={settings.phone} onChange={handleInputChange} placeholder="Numéro de téléphone" />
                </div>

                <div>
                  <Label>Email</Label>
                  <Input type="email" name="email" value={settings.email} onChange={handleInputChange} placeholder="Email de contact" />
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <h4 className="text-lg font-semibold mb-4">Informations bancaires</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>RIB / IBAN</Label>
                    <Input type="text" name="rib" value={settings.rib} onChange={handleInputChange} placeholder="RIB ou IBAN du cabinet" />
                  </div>
                  <div>
                    <Label>Banque</Label>
                    <Input type="text" name="bank" value={settings.bank} onChange={handleInputChange} placeholder="Nom de la banque" />
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <h4 className="text-lg font-semibold mb-4">Paramètres de facturation</h4>
                <div className="space-y-4">
                  <div>
                    <Label>Conditions de paiement par défaut</Label>
                    <Textarea name="paymentTerms" value={settings.paymentTerms} onChange={handleInputChange} placeholder="Ex: Paiement à 30 jours, virement bancaire" />
                  </div>
                  <div>
                    <Label>Notes par défaut sur les factures</Label>
                    <Textarea name="defaultNotes" value={settings.defaultNotes} onChange={handleInputChange} placeholder="Notes à afficher par défaut sur toutes les factures" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'einvoicing' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="text-sm text-blue-700">
                  Ces paramètres sont utilisés <strong>uniquement</strong> lorsque le Cabinet émet ses propres factures d'honoraires.<br/>
                  Pour vos clients, vous devez configurer ces accès dans leur fiche Client respective.
                </p>
              </div>

              <div className="flex items-center mb-6">
                <input id="enableEInvoice" type="checkbox" checked={settings.eInvoicingConfig.enabled} onChange={handleToggleEInvoice} className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                <label htmlFor="enableEInvoice" className="ml-2 block text-lg font-medium text-gray-900">Activer le module E-Facturation pour le Cabinet</label>
              </div>

              {settings.eInvoicingConfig.enabled && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                      <Icon name="IconServer" size={18} className="mr-2 text-indigo-600 inline" /> Tunisie TradeNet (TTN)
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Mode d'envoi</Label>
                        <Select value={settings.eInvoicingConfig.ttn.deliveryMethod} onChange={(e) => handleConfigChange('ttn', 'deliveryMethod', e.target.value)}>
                          <option value="SOAP">Web Service (SOAP)</option>
                          <option value="SFTP">SFTP (Fichier)</option>
                        </Select>
                      </div>
                      <div>
                        <Label>Login Technique TTN</Label>
                        <Input type="text" value={settings.eInvoicingConfig.ttn.username} onChange={(e) => handleConfigChange('ttn', 'username', e.target.value)} placeholder="Ex: 1106519TAM000" />
                      </div>
                      <div>
                        <Label>Mot de passe TTN</Label>
                        <Input type="password" value={settings.eInvoicingConfig.ttn.password} onChange={(e) => handleConfigChange('ttn', 'password', e.target.value)} />
                      </div>
                      <div>
                        <Label>Code PIN Certificat (.p12)</Label>
                        <Input type="password" value={settings.eInvoicingConfig.ttn.certificatePin} onChange={(e) => handleConfigChange('ttn', 'certificatePin', e.target.value)} />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                      <Icon name="IconDeviceMobile" size={18} className="mr-2 text-purple-600 inline" /> DigiGo (Signature Mobile)
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Client ID (App DigiGo)</Label>
                        <Input type="text" value={settings.eInvoicingConfig.digigo.clientId} onChange={(e) => handleConfigChange('digigo', 'clientId', e.target.value)} />
                      </div>
                      <div>
                        <Label>Client Secret</Label>
                        <Input type="password" value={settings.eInvoicingConfig.digigo.clientSecret} onChange={(e) => handleConfigChange('digigo', 'clientSecret', e.target.value)} />
                      </div>
                      <div>
                        <Label>Redirect URI</Label>
                        <Input type="text" value={settings.eInvoicingConfig.digigo.redirectUri} onChange={(e) => handleConfigChange('digigo', 'redirectUri', e.target.value)} placeholder="https://app.votre-domaine.tn/digigo/callback" />
                      </div>
                      <div>
                        <Label>Email Compte DigiGo (Défaut)</Label>
                        <Input type="email" value={settings.eInvoicingConfig.digigo.defaultSignerEmail} onChange={(e) => handleConfigChange('digigo', 'defaultSignerEmail', e.target.value)} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Préférences Système</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Devise</Label>
                  <Select name="currency" value={settings.currency} onChange={handleInputChange}>
                    <option value="TND">TND (Dinar Tunisien)</option>
                    <option value="MAD">MAD (Dirham Marocain)</option>
                    <option value="EUR">EUR (Euro)</option>
                    <option value="USD">USD (Dollar US)</option>
                  </Select>
                </div>
                <div>
                  <Label>Langue</Label>
                  <Select name="language" value={settings.language} onChange={handleInputChange}>
                    <option value="fr">Français</option>
                    <option value="ar">العربية</option>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Notifications par email</Label>
                    <p className="text-sm text-gray-500">Recevoir les alertes et rapports par email</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" name="notifications" checked={settings.notifications} onChange={handleInputChange} className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Sauvegarde automatique</Label>
                    <p className="text-sm text-gray-500">Sauvegarder automatiquement les données quotidiennement</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" name="autoBackup" checked={settings.autoBackup} onChange={handleInputChange} className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Sécurité</h3>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800">Sécurité du compte</h4>
                <p className="text-yellow-700 text-sm">Modifiez régulièrement votre mot de passe pour plus de sécurité</p>
              </div>

              <div className="space-y-4 max-w-md">
                <div>
                  <Label>Mot de passe actuel</Label>
                  <Input type="password" name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} placeholder="Entrez votre mot de passe actuel" />
                </div>
                <div>
                  <Label>Nouveau mot de passe</Label>
                  <Input type="password" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} placeholder="Entrez un nouveau mot de passe" />
                </div>
                <div>
                  <Label>Confirmer le mot de passe</Label>
                  <Input type="password" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} placeholder="Confirmez le nouveau mot de passe" />
                </div>
                <Button variant="primary" onClick={handlePasswordUpdate} disabled={loading}>
                  {loading ? 'Chargement...' : 'Changer le mot de passe'}
                </Button>
              </div>

              <div className="mt-8 pt-4 border-t border-gray-200">
                <h4 className="text-md font-semibold mb-2">Comptes clients existants</h4>
                <p className="text-sm text-gray-600 mb-3">Générer automatiquement des comptes utilisateur pour tous les clients déjà créés (avec email et matricule fiscale).</p>
                <Button variant="primary" onClick={handleGenerateClientAccounts}>
                  Générer les comptes clients existants
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-4">
        <Button variant="secondary">Annuler</Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
        </Button>
      </div>
    </div>
  );
}